import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, MapPin, UserPlus, UserMinus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminGroupsPanel = ({ token }) => {
  const [myGroups, setMyGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [membersLoading, setMembersLoading] = useState(false);
  const [editingWorkHours, setEditingWorkHours] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const DAYS = [
    { id: 1, label: 'B.e' }, { id: 2, label: 'Ç.a' }, { id: 3, label: 'Ç' },
    { id: 4, label: 'C.a' }, { id: 5, label: 'C' }, { id: 6, label: 'Ş' }, { id: 7, label: 'B' }
  ];

  const saveWorkHours = async (groupId) => {
    if (!editingWorkHours) return;
    try {
      await axios.put(`${API}/groups/${groupId}`, {
        work_start: editingWorkHours.work_start,
        work_end: editingWorkHours.work_end,
        work_days: editingWorkHours.work_days
      }, { headers });
      toast.success('İş saatları yeniləndi');
      setEditingWorkHours(null);
      fetchMyGroups();
    } catch (error) {
      toast.error('İş saatları yenilənmədi');
    }
  };

  useEffect(() => {
    fetchMyGroups();
    fetchAllUsers();
  }, []);

  const fetchMyGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups/my-admin-groups`, { headers });
      setMyGroups(response.data);
    } catch (error) {
      console.error('Groups fetch error:', error);
      toast.error('Qrupları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchGroupMembers = async (groupId) => {
    setMembersLoading(true);
    try {
      const response = await axios.get(`${API}/groups/${groupId}/members`, { headers });
      setGroupMembers(response.data);
    } catch (error) {
      toast.error('Üzvləri yükləmək mümkün olmadı');
      setGroupMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const openMembersDialog = (group) => {
    setSelectedGroup(group);
    fetchGroupMembers(group.id);
    setShowMembersDialog(true);
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !selectedGroup) return;
    try {
      await axios.post(`${API}/groups/${selectedGroup.id}/members/${selectedUserId}`, {}, { headers });
      toast.success('Üzv əlavə edildi');
      setShowAddMemberDialog(false);
      setSelectedUserId('');
      fetchGroupMembers(selectedGroup.id);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Üzv əlavə etmək mümkün olmadı');
    }
  };

  const handleRemoveMember = async (userId, username) => {
    if (!window.confirm(`${username} qrupdan silinsin?`)) return;
    try {
      await axios.delete(`${API}/groups/${selectedGroup.id}/members/${userId}`, { headers });
      toast.success('Üzv qrupdan silindi');
      fetchGroupMembers(selectedGroup.id);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Üzvü silmək mümkün olmadı');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'operator': return 'bg-blue-100 text-blue-700';
      case 'accountant': return 'bg-purple-100 text-purple-700';
      case 'd2d_agent': return 'bg-orange-100 text-orange-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'operator': return 'Operator';
      case 'accountant': return 'Mühasib';
      case 'd2d_agent': return 'D2D Agent';
      case 'technician': return 'Texnik';
      default: return role;
    }
  };

  // Filter users that can be added to the group (not already members and not superadmin/accountant)
  const availableUsers = allUsers.filter(u => {
    // Don't show superadmins or accountants
    if (u.is_superadmin || u.role === 'superadmin' || u.role === 'accountant') return false;
    // Don't show users already in this group
    if (selectedGroup && u.groups && u.groups.includes(selectedGroup.id)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-zinc-900">Mənim Qruplarım</h2>
        <Button variant="outline" onClick={fetchMyGroups}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenilə
        </Button>
      </div>

      {myGroups.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <MapPin className="h-12 w-12 mx-auto mb-4 text-zinc-300" />
            <p className="text-zinc-500">Sizə təyin olunmuş qrup yoxdur</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {myGroups.map(group => (
            <Card key={group.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  {group.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm text-zinc-500 mb-2">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {group.member_count || 0} üzv
                  </span>
                </div>
                {/* İş saatları */}
                <div className="text-xs text-zinc-500 mb-3 p-2 bg-zinc-50 rounded">
                  <div className="flex items-center justify-between">
                    <span>İş saatı: <strong>{group.work_start || '09:00'} - {group.work_end || '18:00'}</strong></span>
                    <button
                      onClick={() => setEditingWorkHours({
                        groupId: group.id,
                        work_start: group.work_start || '09:00',
                        work_end: group.work_end || '18:00',
                        work_days: group.work_days || [1,2,3,4,5]
                      })}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Dəyiş
                    </button>
                  </div>
                  <div className="flex gap-1 mt-1">
                    {DAYS.map(d => (
                      <span key={d.id} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${(group.work_days || [1,2,3,4,5]).includes(d.id) ? 'bg-blue-100 text-blue-700' : 'bg-zinc-200 text-zinc-400'}`}>
                        {d.label}
                      </span>
                    ))}
                  </div>
                </div>
                {/* İş saatı redaktə */}
                {editingWorkHours?.groupId === group.id && (
                  <div className="mb-3 p-2 border border-blue-200 rounded bg-blue-50 space-y-2">
                    <div className="flex gap-2">
                      <input type="time" value={editingWorkHours.work_start} onChange={e => setEditingWorkHours({...editingWorkHours, work_start: e.target.value})} className="text-xs border rounded px-2 py-1" />
                      <span className="text-xs self-center">-</span>
                      <input type="time" value={editingWorkHours.work_end} onChange={e => setEditingWorkHours({...editingWorkHours, work_end: e.target.value})} className="text-xs border rounded px-2 py-1" />
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {DAYS.map(d => (
                        <button key={d.id} onClick={() => {
                          const days = editingWorkHours.work_days.includes(d.id) ? editingWorkHours.work_days.filter(x => x !== d.id) : [...editingWorkHours.work_days, d.id];
                          setEditingWorkHours({...editingWorkHours, work_days: days});
                        }} className={`px-2 py-0.5 rounded text-[10px] font-medium ${editingWorkHours.work_days.includes(d.id) ? 'bg-blue-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveWorkHours(group.id)} className="text-xs h-7">Yadda saxla</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingWorkHours(null)} className="text-xs h-7">Ləğv</Button>
                    </div>
                  </div>
                )}
                <Button 
                  onClick={() => openMembersDialog(group)}
                  className="w-full"
                  variant="outline"
                  data-testid={`view-members-${group.id}`}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Üzvləri Gör
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {selectedGroup?.name} - Üzvlər
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setShowAddMemberDialog(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="add-member-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Üzv Əlavə Et
              </Button>
            </div>

            {membersLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : groupMembers.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                Bu qrupda üzv yoxdur
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-zinc-600">İstifadəçi</th>
                      <th className="text-left p-3 text-sm font-medium text-zinc-600">Rol</th>
                      <th className="text-left p-3 text-sm font-medium text-zinc-600">Telefon</th>
                      <th className="text-right p-3 text-sm font-medium text-zinc-600">Əməliyyat</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {groupMembers.map(member => (
                      <tr key={member.id} className="hover:bg-zinc-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{member.full_name || member.username}</p>
                            <p className="text-sm text-zinc-500">@{member.username}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(member.role)}`}>
                            {getRoleLabel(member.role)}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-zinc-600">
                          {member.phone || '-'}
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRemoveMember(member.id, member.username)}
                            data-testid={`remove-member-${member.id}`}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMembersDialog(false)}>
              Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Qrupa Üzv Əlavə Et</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>İstifadəçi seçin</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="mt-1.5" data-testid="select-user-dropdown">
                  <SelectValue placeholder="İstifadəçi seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <div className="p-2 text-sm text-zinc-500">Əlavə ediləcək istifadəçi yoxdur</div>
                  ) : (
                    availableUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name || user.username} ({getRoleLabel(user.role)})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddMemberDialog(false); setSelectedUserId(''); }}>
              Ləğv et
            </Button>
            <Button 
              onClick={handleAddMember} 
              disabled={!selectedUserId}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-add-member"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Əlavə Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGroupsPanel;
